import React, { useState } from "react";
import {
  useGetAdminDashboard,
  useGetOperationsDashboard,
  useUpdateApplication,
  getGetOperationsDashboardQueryKey,
  type Application,
  type AdminDashboard as AdminDashboardData,
} from "@workspace/api-client-react";
import type { QueryKey } from "@tanstack/react-query";
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
  Calendar,
  Briefcase,
  Users,
  Hash,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { getGetAdminDashboardQueryKey } from "@workspace/api-client-react";

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object" && "error" in data) {
      const message = (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

function OpenserveIdEditor({
  app,
  invalidateKey,
}: {
  app: Application;
  invalidateKey: QueryKey;
}) {
  const current = app.openserveAgentId ?? "";
  const [value, setValue] = useState(current);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutate, isPending } = useUpdateApplication();

  const dirty = value.trim() !== current;
  const trimmed = value.trim();
  const prefixOk = trimmed === "" || trimmed.startsWith("MOO");

  const save = () => {
    const next = value.trim();
    if (next && !next.startsWith("MOO")) return;
    mutate(
      { id: app.id, data: { openserveAgentId: next ? next : null } },
      {
        onSuccess: () => {
          toast({ title: "Openserve Agent ID saved" });
          queryClient.invalidateQueries({ queryKey: invalidateKey });
        },
        onError: (err) => {
          toast({
            title: "Could not save Openserve Agent ID",
            description: apiErrorMessage(err, "Please try again."),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold text-[#0B132B] flex items-center gap-2">
          <Hash className="w-5 h-5 text-[#0B132B]" />
          Openserve Agent ID
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-slate-500">
          Copy the agent's unique ID from the Openserve portal and paste it
          here. Openserve IDs start with &quot;MOO&quot;. It must be assigned
          before this application can be approved, and cannot be shared with
          another agent.
        </p>
        <div className="flex items-center gap-2 max-w-sm">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. MOOJEA303"
            className="bg-white"
          />
          <Button
            variant="outline"
            disabled={!dirty || !prefixOk || isPending}
            onClick={save}
          >
            Save
          </Button>
        </div>
        {trimmed !== "" && !prefixOk && (
          <p className="text-sm text-amber-600">
            Openserve Agent ID must start with &quot;MOO&quot;.
          </p>
        )}
        {!current && prefixOk && (
          <p className="text-sm text-amber-600">
            Not yet assigned — approval is blocked until an Openserve Agent ID
            is added.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ApplicationReviewDashboard({
  dashboard,
  isLoading,
  error,
  invalidateKey,
}: {
  dashboard: AdminDashboardData | undefined;
  isLoading: boolean;
  error: unknown;
  invalidateKey: QueryKey;
}) {
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const { mutate: updateApplication } = useUpdateApplication();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-20 bg-gray-200 rounded"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error || !dashboard) {
    return <div className="text-red-500">Failed to load dashboard.</div>;
  }

  const selectedApp =
    dashboard.applications.find((a) => a.id === selectedAppId) ||
    dashboard.applications[0];

  const handleStatusUpdate = (status: any) => {
    if (!selectedApp) return;
    updateApplication(
      { id: selectedApp.id, data: { status } },
      {
        onSuccess: () => {
          toast({
            title: "Application Updated",
            description: `Status changed to ${status}`,
          });
          queryClient.invalidateQueries({ queryKey: invalidateKey });
        },
        onError: (err) => {
          toast({
            title: "Action blocked",
            description: apiErrorMessage(
              err,
              "Could not update the application.",
            ),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Top Header */}
      <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between shadow-sm flex-shrink-0 mb-4 rounded-xl">
        <div>
          <h1 className="text-lg font-semibold text-[#0B132B]">
            Application Review Queue
          </h1>
          <p className="text-sm text-slate-500">
            Review and verify new Openserve fibre agents
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search applicants..."
              className="w-64 pl-9 bg-slate-50 border-slate-200"
            />
          </div>
        </div>
      </header>

      {/* Split Screen Workspace */}
      <div className="flex flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* LEFT LIST: Queue */}
        <div className="w-[420px] flex flex-col border-r border-slate-200 flex-shrink-0">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-[#0B132B]">
                Pending Review
              </span>
              <Badge
                variant="secondary"
                className="bg-[#E6007E]/10 text-[#E6007E] hover:bg-[#E6007E]/20"
              >
                {dashboard.pendingReview}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500"
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-3 flex flex-col gap-2">
              {dashboard.applications.map((app) => (
                <div
                  key={app.id}
                  onClick={() => setSelectedAppId(app.id)}
                  className={cn(
                    "p-4 rounded-xl cursor-pointer transition-all border text-left flex flex-col gap-3 group relative overflow-hidden",
                    selectedAppId === app.id ||
                      (!selectedAppId && selectedApp?.id === app.id)
                      ? "bg-slate-50 border-[#0B132B] shadow-sm ring-1 ring-[#0B132B]/10"
                      : "bg-white border-slate-200 hover:border-[#0B132B]/30 hover:shadow-sm",
                  )}
                >
                  {(selectedAppId === app.id ||
                    (!selectedAppId && selectedApp?.id === app.id)) && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0B132B]" />
                  )}

                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-[#0B132B] truncate max-w-[220px]">
                        {app.applicantName}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-3 h-3" />{" "}
                          {app.agentType || "Agent"}
                        </span>
                        <span>•</span>
                        <span>APP-{app.id}</span>
                      </div>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {app.submittedAt
                        ? new Date(app.submittedAt).toLocaleDateString()
                        : "N/A"}
                    </div>
                  </div>
                </div>
              ))}
              {dashboard.applications.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No applications to review.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT DETAIL: Selected Application */}
        {selectedApp ? (
          <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/50">
            {/* Detail Header */}
            <div className="h-20 bg-white border-b border-slate-200 px-8 flex items-center justify-between flex-shrink-0 shadow-sm z-10">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12 border-2 border-slate-100">
                  <AvatarFallback className="bg-slate-200 text-[#0B132B] text-lg">
                    {selectedApp.applicantName
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-xl font-bold text-[#0B132B]">
                    {selectedApp.applicantName}
                  </h2>
                  <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                    <span>APP-{selectedApp.id}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span>
                      Submitted:{" "}
                      {selectedApp.submittedAt
                        ? new Date(selectedApp.submittedAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={() => handleStatusUpdate("rejected")}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 bg-white shadow-sm font-medium"
                >
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button
                  onClick={() => handleStatusUpdate("action_required")}
                  variant="outline"
                  className="border-amber-200 text-amber-600 hover:bg-amber-50 bg-white shadow-sm font-medium"
                >
                  <AlertCircle className="w-4 h-4 mr-2" /> Request Resubmission
                </Button>
                <Button
                  onClick={() => handleStatusUpdate("approved")}
                  disabled={!selectedApp.openserveAgentId?.trim()}
                  title={
                    selectedApp.openserveAgentId?.trim()
                      ? undefined
                      : "Assign an Openserve Agent ID before approving"
                  }
                  className="bg-[#00A859] hover:bg-[#00A859]/90 text-white shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
              </div>
            </div>

            {/* Detail Content Scroll */}
            <ScrollArea className="flex-1 p-8">
              <div className="max-w-3xl mx-auto flex flex-col gap-8">
                {selectedApp.status !== "pending" && (
                  <div className="p-4 rounded-xl border bg-white flex items-start gap-3">
                    <StatusBadge status={selectedApp.status} />
                    <div>
                      <h4 className="font-medium text-slate-700">
                        Current Status: {selectedApp.status.replace("_", " ")}
                      </h4>
                      {selectedApp.reviewerNotes && (
                        <p className="text-sm text-slate-600 mt-1">
                          {selectedApp.reviewerNotes}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base font-semibold text-[#0B132B] flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#E6007E]" />
                      Personal Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Full Name
                      </label>
                      <div className="font-medium text-slate-900 mt-1">
                        {selectedApp.applicantName}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Email
                      </label>
                      <div className="font-medium text-slate-900 mt-1">
                        {selectedApp.email || "-"}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Phone
                      </label>
                      <div className="font-medium text-slate-900 mt-1">
                        {selectedApp.phone || "-"}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Agent Type
                      </label>
                      <div className="font-medium text-slate-900 mt-1">
                        {selectedApp.agentType || "-"}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <OpenserveIdEditor
                  key={selectedApp.id}
                  app={selectedApp}
                  invalidateKey={invalidateKey}
                />
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            Select an application to view details.
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { data: dashboard, isLoading, error } = useGetAdminDashboard();
  return (
    <ApplicationReviewDashboard
      dashboard={dashboard}
      isLoading={isLoading}
      error={error}
      invalidateKey={getGetAdminDashboardQueryKey()}
    />
  );
}

export function OperationsDashboard() {
  const { data: dashboard, isLoading, error } = useGetOperationsDashboard();
  return (
    <ApplicationReviewDashboard
      dashboard={dashboard}
      isLoading={isLoading}
      error={error}
      invalidateKey={getGetOperationsDashboardQueryKey()}
    />
  );
}
